<template>
  <div class="text-center">
    <div class="card mb-2">
      <div class="card-header">Progress</div>
      <div class="card-body">
        <h2 class="card-title">{{ formattedMonth }}: {{ total }} hours</h2>
        <div class="d-flex align-items-center gap-2">
          <div class="progress flex-grow-1" style="height: 30px">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              :aria-valuenow="10"
              aria-valuemin="0"
              :aria-valuemax="goal.goalValue"
              :style="{
                width: (total / goal.goalValue) * 100 + '%',
              }"
              v-bind:class="goalReached"
            ></div>
          </div>
          <span class="text-muted ml-2">{{ goal.goalValue }} hours</span>
        </div>
        <router-link
          :to="
            goalExists
              ? { name: 'editgoal', params: { month: month } }
              : { name: 'creategoal', params: { month: month } }
          "
          class="btn btn-secondary mt-4 float-end"
          >{{ goalButtonText }}</router-link
        >
      </div>
    </div>

    <table class="table" style="table-layout: fixed; width: 100%">
      <thead>
        <tr>
          <th scope="col">Date</th>
          <th scope="col">Time</th>
          <th scope="col">Partners</th>
          <th scope="col"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="event in events" :key="event._id">
          <th scope="row">{{ formatDate(event.date) }}</th>
          <td>{{ event.time }} hours</td>
          <td class="partner-col">{{ event.partners?.join(", ") }}</td>
          <td>
            <router-link
              :to="{ name: 'editevent', params: { id: event._id } }"
              class="btn btn-secondary mr-2"
              ><svg
                xmlns="http://www.w3.org/2000/svg"
                id="Outline"
                viewBox="0 0 24 24"
                width="25"
                height="25"
                fill="white"
              >
                <path
                  d="M22.853,1.148a3.626,3.626,0,0,0-5.124,0L1.465,17.412A4.968,4.968,0,0,0,0,20.947V23a1,1,0,0,0,1,1H3.053a4.966,4.966,0,0,0,3.535-1.464L22.853,6.271A3.626,3.626,0,0,0,22.853,1.148ZM5.174,21.122A3.022,3.022,0,0,1,3.053,22H2V20.947a2.98,2.98,0,0,1,.879-2.121L15.222,6.483l2.3,2.3ZM21.438,4.857,18.932,7.364l-2.3-2.295,2.507-2.507a1.623,1.623,0,1,1,2.295,2.3Z"
                />
              </svg>
            </router-link>
            <button class="btn btn-danger" @click="initialDeletion(event._id)">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                id="Outline"
                viewBox="0 0 24 24"
                width="25"
                height="25"
                fill="white"
              >
                <path
                  d="M21,4H17.9A5.009,5.009,0,0,0,13,0H11A5.009,5.009,0,0,0,6.1,4H3A1,1,0,0,0,3,6H4V19a5.006,5.006,0,0,0,5,5h6a5.006,5.006,0,0,0,5-5V6h1a1,1,0,0,0,0-2ZM11,2h2a3.006,3.006,0,0,1,2.829,2H8.171A3.006,3.006,0,0,1,11,2Zm7,17a3,3,0,0,1-3,3H9a3,3,0,0,1-3-3V6H18Z"
                />
                <path
                  d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18Z"
                />
                <path
                  d="M14,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                />
              </svg>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="alert alert-danger" role="alert" v-if="fetchError">
      {{ errorMessage }}
    </div>

    <div class="deleteConfirmation" v-if="deleteConfirmationVisible">
      <div class="card m-2 deleteConfirmationCard">
        <h4>Are you sure you want to delete this event?</h4>
        <div class="row">
          <div class="col">
            <button
              class="btn btn-secondary"
              @click="deleteConfirmationVisible = false"
            >
              Cancel
            </button>
          </div>
          <div class="col">
            <button class="btn btn-danger" @click="deleteEvent">Delete</button>
          </div>
        </div>
      </div>
    </div>

    <router-link class="button btn-primary float" to="/event">+</router-link>
  </div>
</template>

<style scoped>
.float {
  position: fixed;
  bottom: 60px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  font-size: 2.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  text-decoration: none;
}

.deleteConfirmation {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.deleteConfirmationCard {
  padding: 25px;
}

@media (max-width: 768px) {
  .partner-col {
    font-size: small;
  }
}
</style>

<script>
import axios from "axios";
import { server, addSpaces, formatDate, formatMonth } from "@/utils/helper";
import router from "@/router";

export default {
  props: {
    usedMonth: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      events: [],
      goal: [],
      total: 0.0,
      month: null,
      formattedMonth: "",
      fetchError: false,
      deleteConfirmationVisible: false,
      errorMessage: "",
      goalButtonText: "Edit Goal",
      goalExists: true,
      eventToDelete: null,
    };
  },
  async created() {
    this.month = this.activeMonth;
    this.formattedMonth = formatMonth(this.month);
    console.log(this.month);
    await this.fetchEvents(this.activeMonth);
    await this.fetchGoal(this.activeMonth);
    await this.fetchMonth(this.activeMonth);
  },
  computed: {
    activeMonth() {
      return this.usedMonth ?? this.$route.params.month;
    },
    goalReached() {
      if (this.total > this.goal.goalValue) {
        return "bg-success";
      }
      return 0;
    },
  },
  methods: {
    addSpaces,
    formatDate,
    async fetchEvents(month) {
      const bearerToken = localStorage.getItem("jwt");
      if (!bearerToken) {
        this.fetchError = true;
        this.errorMessage = "Please log in to see events.";
        router.push("/login");
      }
      await axios
        .get(`${server.baseUrl}/events/months/${month}`, {
          headers: { Authorization: "Bearer " + bearerToken },
          redact: ["authorization"],
        })
        .then((data) => (this.events = data.data))
        .catch((error) => {
          this.fetchError = true;
          if (error.response.status == 404) {
            this.errorMessage = "No data for this month!";
          }
        });
      this.events.sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    async fetchGoal(month) {
      const bearerToken = localStorage.getItem("jwt");
      if (!bearerToken) {
        this.fetchError = true;
        this.errorMessage = "Please log in to see events.";
        router.push("/login");
      }

      await axios
        .get(`${server.baseUrl}/goals/${month}`, {
          headers: { Authorization: "Bearer " + bearerToken },
          redact: ["authorization"],
        })
        .then((data) => (this.goal = data.data))
        .catch((error) => {
          console.log(error);
          this.fetchError = true;
          if (error.response.status == 404) {
            this.errorMessage = "No data for this month!";
            this.goalExists = false;
            this.goalButtonText = "Create Goal";
          }
        });
    },
    async fetchMonth(month) {
      const bearerToken = localStorage.getItem("jwt");
      if (!bearerToken) {
        this.fetchError = true;
        this.errorMessage = "Please log in to see events.";
        router.push("/login");
      }

      await axios
        .get(`${server.baseUrl}/months/${month}`, {
          headers: { Authorization: "Bearer " + bearerToken },
          redact: ["authorization"],
        })
        .then((data) => (this.total = data.data.time))
        .catch((error) => {
          console.log(error);
          this.fetchError = true;
          if (error.response.status == 404) {
            this.errorMessage = "No data for this month!";
          }
        });
    },
    initialDeletion(id) {
      this.deleteConfirmationVisible = true;
      this.eventToDelete = id;
    },
    deleteEvent() {
      const bearerToken = localStorage.getItem("jwt");
      axios.delete(`${server.baseUrl}/events/${this.eventToDelete}`, {
        headers: { Authorization: "Bearer " + bearerToken },
        redact: ["authorization"],
      });
      window.location.reload();
    },
  },
};
</script>
